/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type SyntheticEvent } from 'react';
import { type WithIntlProps, defineMessage } from '@atlassian/jira-intl';
import noop from 'lodash/noop';
import { SERVICE_DESK_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { ModalDialog } from '@jira/software__modal-dialog/src/ModalDialog.tsx';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import Button from '@atlaskit/button/button';
import LoadingButton from '@atlaskit/button/loading-button';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { expVal } from '@atlassian/jira-feature-experiments';
import ShortcutScope from '@jira/platform__common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { withFlagService, type FlagService } from '@atlassian/jira-flags';
import { injectIntl, type IntlShape } from '@atlassian/jira-intl/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	fireTrackAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useProjectRestoreService } from '../services/index.tsx';
import { ModalMessageContent } from './modal-message/index.tsx';
import type { Props, PropsWithIntlAndFlagService } from './types.tsx';
import { getDurationInTrash } from './utils.tsx';

/* eslint-disable jira/i18n/no-export-define-message */
/* eslint-disable jira/filename-case */
// eslint-disable-next-line no-restricted-imports
const modalHeader: { id: string; defaultMessage: string; description: string } = defineMessage({
	id: 'project-restore-modal.modal-header',
	defaultMessage: 'Restore project?',
	description: 'The modal header for project restore action',
});

const jsmModalHeader: { id: string; defaultMessage: string; description: string } = defineMessage({
	id: 'project-restore-modal.jsm-modal-header',
	defaultMessage: 'Restore this service project?',
	description: 'The modal header for project restore action',
});

const confirmRestoreButton: { id: string; defaultMessage: string; description: string } =
	defineMessage({
		id: 'project-restore-modal.confirm-restore-button',
		defaultMessage: 'Restore',
		description: 'Confirmation of project restore action',
	});

const cancelRestoreButton: { id: string; defaultMessage: string; description: string } =
	defineMessage({
		id: 'project-restore-modal.cancel-restore-button',
		defaultMessage: 'Cancel',
		description: 'Cancellation of project restore action',
	});

const restoreSuccessMessageTitle: { id: string; defaultMessage: string; description: string } =
	defineMessage({
		id: 'project-restore-modal.restore-success-message-title',
		defaultMessage: 'Project successfully restored',
		description: 'Title for success message when project is restored',
	});

const restoreServiceDeskSuccessMessageTitle: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-service-desk-success-message-title',
	defaultMessage: 'Service project restored',
	description: 'Title for success message when a service desk project is restored',
});

const restoreServiceDeskSuccessMessageContent: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-service-desk-success-message-content',
	defaultMessage:
		'You may need to turn on email support for your service project to work as normal.',
	description: 'Content for success message when a service desk project is restored',
});

const restoreServiceDeskSuccessMessageActionEmailSettings: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-service-desk-success-message-action-email-settings',
	defaultMessage: 'Go to email settings',
	description:
		'Title for action to go to email settings on success message when a service desk project is restored',
});

const restoreErrorMessageTitle: { id: string; defaultMessage: string; description: string } =
	defineMessage({
		id: 'project-restore-modal.restore-error-message-title',
		defaultMessage: 'Something went wrong',
		description: 'Title for error message when project restore is failed',
	});

const restoreErrorMessageDescription: { id: string; defaultMessage: string; description: string } =
	defineMessage({
		id: 'project-restore-modal.restore-error-message-description',
		defaultMessage: 'We couldn’t restore the “{projectName}” project. Try again.',
		description: 'Description for error message when project restore is failed',
	});

const restoreErrorMessageDescriptionRenderError: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-error-message-description-render-error',
	defaultMessage: 'We couldn’t restore the “{projectName}” project.{br}{error}',
	description:
		'Description for error message when project restore is failed. Includes the error that comes from the server',
});

const restoreSuccessMessageTitleGalaxia: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-success-message-title-galaxia',
	defaultMessage: 'Space successfully restored',
	description: 'Title for success message when project is restored',
});

const restoreServiceDeskSuccessMessageTitleGalaxia: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-service-desk-success-message-title-galaxia',
	defaultMessage: 'Service space restored',
	description: 'Title for success message when a service desk project is restored',
});

const restoreServiceDeskSuccessMessageContentGalaxia: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-service-desk-success-message-content-galaxia',
	defaultMessage: 'You may need to turn on email support for your service space to work as normal.',
	description: 'Content for success message when a service desk project is restored',
});

const restoreErrorMessageDescriptionGalaxia: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-error-message-description-galaxia',
	defaultMessage: 'We couldn’t restore the “{projectName}” space. Try again.',
	description: 'Description for error message when project restore is failed',
});

const restoreErrorMessageDescriptionRenderErrorGalaxia: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'project-restore-modal.restore-error-message-description-render-error-galaxia',
	defaultMessage: 'We couldn’t restore the “{projectName}” space.{br}{error}',
	description:
		'Description for error message when project restore is failed. Includes the error that comes from the server',
});

const modalHeaderGalaxiaMessage: { id: string; defaultMessage: string; description: string } =
	defineMessage({
		id: 'project-restore-modal.modal-header-galaxia',
		defaultMessage: 'Restore space?',
		description: 'The modal header for project restore action',
	});

const jsmModalHeaderGalaxiaMessage: { id: string; defaultMessage: string; description: string } =
	defineMessage({
		id: 'project-restore-modal.jsm-modal-header-galaxia',
		defaultMessage: 'Restore this service space?',
		description: 'The modal header for project restore action',
	});

export const RESTORE_FROM_TRASH = 'restore-from-trash' as const;
export const RESTORE_FROM_ARCHIVE = 'restore-from-archive' as const;
export type ProjectRestoreType = typeof RESTORE_FROM_TRASH | typeof RESTORE_FROM_ARCHIVE;

export const ProjectRestoreModal = (props: PropsWithIntlAndFlagService): React.JSX.Element => {
	const jsmModalHeaderGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? jsmModalHeaderGalaxiaMessage
		: jsmModalHeader;
	const modalHeaderGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? modalHeaderGalaxiaMessage
		: modalHeader;
	const {
		isProjectSimplified,
		projectType,
		projectName,
		projectId,
		projectKey,
		projectInactiveDate,
		intl: { formatMessage },
		onClose,
		onProjectRestoreSuccess,
		onProjectRestoreFailure,
		flagService: { showFlag },
	} = props;

	const [isRestoring, restoreProject] = useProjectRestoreService({
		projectId,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const attributes = {
		projectType,
		isProjectSimplified,
	};

	const onSuccess = (): void => {
		const analyticsEvent: UIAnalyticsEvent = createAnalyticsEvent({
			action: 'restored',
		});
		fireTrackAnalytics(analyticsEvent, 'project restored', projectId.toString(), {
			...attributes,
			durationInTrash: projectInactiveDate ? getDurationInTrash(projectInactiveDate) : undefined,
		});
		onProjectRestoreSuccess();
		if (projectType === SERVICE_DESK_PROJECT) {
			showFlag({
				messageId: 'project-restore-modal.ui.show-flag.success.restore-service-desk-success',
				messageType: 'transactional',
				type: 'success',
				title: expVal('project-terminology-refresh', 'isEnabled', false)
					? restoreServiceDeskSuccessMessageTitleGalaxia
					: restoreServiceDeskSuccessMessageTitle,
				description: expVal('project-terminology-refresh', 'isEnabled', false)
					? restoreServiceDeskSuccessMessageContentGalaxia
					: restoreServiceDeskSuccessMessageContent,
				testId: 'project-restore-modal.ui.flags.success-flag',
				actions: [
					{
						content: restoreServiceDeskSuccessMessageActionEmailSettings,
						href: `/jira/servicedesk/projects/${projectKey}/settings/email`,
					},
				],
			});
		} else {
			showFlag({
				messageId: 'project-restore-modal.ui.show-flag.success.restore-success',
				messageType: 'transactional',
				type: 'success',
				title: expVal('project-terminology-refresh', 'isEnabled', false)
					? restoreSuccessMessageTitleGalaxia
					: restoreSuccessMessageTitle,
				testId: 'project-restore-modal.ui.flags.success-flag',
			});
		}
	};

	const onFailure = (error: Error | FetchError): void => {
		onProjectRestoreFailure(error);

		if (
			// oxlint-disable-next-line typescript/consistent-type-assertions
			(error as FetchError).statusCode?.toString().startsWith('4') &&
			error.message
		) {
			showFlag({
				messageId: 'project-restore-modal.ui.show-flag.error.restore-error',
				messageType: 'transactional',
				type: 'error',
				title: [restoreErrorMessageTitle, { projectName }],
				description: [
					expVal('project-terminology-refresh', 'isEnabled', false)
						? restoreErrorMessageDescriptionRenderErrorGalaxia
						: restoreErrorMessageDescriptionRenderError,
					{
						projectName,
						error: error.message,
						br: () => <br />,
					},
				],
				testId: 'project-restore-modal.ui.flags.failure-flag',
			});
		} else {
			showFlag({
				messageId: 'project-restore-modal.ui.show-flag.error.restore-error.1',
				messageType: 'transactional',
				type: 'error',
				title: [restoreErrorMessageTitle, { projectName }],
				description: [
					expVal('project-terminology-refresh', 'isEnabled', false)
						? restoreErrorMessageDescriptionGalaxia
						: restoreErrorMessageDescription,
					{ projectName },
				],
				testId: 'project-restore-modal.ui.flags.failure-flag',
			});
		}
	};

	const onRestore = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
		fireUIAnalytics(analyticsEvent, 'restoreProjectButton');
		restoreProject({ onSuccess, onFailure });
	};

	// Unstable components cause bugs and performance problems - please fix this! go/unstable-components
	// eslint-disable-next-line react/no-unstable-nested-components
	const ModalDialogDescription = () => (
		// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
		<p>
			<ModalMessageContent projectType={projectType} />
		</p>
	);

	const onCancelOrNoop = !isRestoring ? onClose : noop;

	return (
		<ContextualAnalyticsData
			sourceName="restoreProject"
			sourceType={MODAL}
			attributes={attributes}
			containerId={`${projectId}`}
			containerType="project"
		>
			<ShortcutScope>
				<ModalDialog
					messageId="project-restore-modal.ui.modal-dialog"
					messageType="transactional"
					onClose={onCancelOrNoop}
					width="small"
				>
					<ModalHeader hasCloseButton>
						<ModalTitle>
							{formatMessage(
								projectType === SERVICE_DESK_PROJECT ? jsmModalHeaderGalaxia : modalHeaderGalaxia,
								{ projectName },
							)}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<FireScreenAnalytics />
						<ModalDialogDescription />
					</ModalBody>
					<ModalFooter>
						<Button
							appearance="subtle-link"
							onClick={onCancelOrNoop}
							isDisabled={isRestoring}
							testId="project-restore-modal.ui.close-button-wrapper"
						>
							{formatMessage(cancelRestoreButton)}
						</Button>
						<LoadingButton
							appearance="primary"
							onClick={onRestore}
							isDisabled={isRestoring}
							isLoading={isRestoring}
							testId="project-restore-modal.ui.restore-button-wrapper"
						>
							{formatMessage(confirmRestoreButton)}
						</LoadingButton>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		</ContextualAnalyticsData>
	);
};

export const defaultProps: {
	readonly isRestoring: false;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	readonly onClose: (...args: any[]) => void;
} = {
	isRestoring: false,
	onClose: noop,
} as const;

const ExportedComponent: React.FC<
	WithIntlProps<
		Pick<
			Omit<
				Props & {
					intl: IntlShape;
					flagService: FlagService;
				},
				'flagService'
			>,
			'intl' | keyof Props
		> & {} & {}
	>
> & {
	WrappedComponent: React.ComponentType<
		Pick<
			Omit<
				Props & {
					intl: IntlShape;
					flagService: FlagService;
				},
				'flagService'
			>,
			'intl' | keyof Props
		> & {} & {}
	>;
} = injectIntl(withFlagService(ProjectRestoreModal));

export default ExportedComponent;
